import {
    connect as reactReduxConnect,
    useDispatch as reactReduxUseDispatch,
    useSelector as reactReduxUseSelector,
} from 'react-redux';
import {applyMiddleware, combineReducers, compose, createStore} from 'redux';
import {composeWithDevTools} from 'redux-devtools-extension';
import reduxThunk from 'redux-thunk';

import {routerMiddleware} from 'stores/connected-react-router/middleware';
import {connectRouter} from 'stores/connected-react-router/reducer';

import apiMiddleware from './middleware/api';
import externalMiddleware from './middleware/external-calls';
import socketIOMiddleware from './middleware/sockets';

import type {ViewerStore} from 'components/viewer/viewer-reducer';
import type {History} from 'history';
import type {Connect} from 'react-redux';
import type {UseSelectorOptions} from 'react-redux/src/hooks/useSelector';
import type {EqualityFn} from 'react-redux/src/types';
import type {Dispatch} from 'redux';
import type {Action, AnyAction, Reducer, ReducersMapObject, Store} from 'redux';
import type {AppStore} from 'stores/app-store';
import type {IntlStore} from 'stores/intl-store';
import type {ModalsStore} from 'stores/modals-store';
import type {SessionStore} from 'stores/session-store';
import type {PublicSessionViewerStore} from 'views/authorized-anonymous/stores/public-session-viewer-store';
import type {ExamsErrorsStore} from 'views/exams/stores/exams-errors-store';
import type {ExamModalsStore} from 'views/exams/stores/modals-store';
import type {CourseAgendaCalendarStore} from 'views/signed-in/courses/stores/course-agenda-calendar-store';
import type {CourseEditAdminsStore} from 'views/signed-in/courses/stores/course-edit-admins-store';
import type {CourseEditAttachmentsStore} from 'views/signed-in/courses/stores/course-edit-attachments-store';
import type {CourseEvalTemplateEvalsStore} from 'views/signed-in/courses/stores/course-eval-template-evals-store';
import type {CourseEvaluationEditStore} from 'views/signed-in/courses/stores/course-evaluation-edit-store';
import type {CourseEvaluationTemplateEditSummaryStore} from 'views/signed-in/courses/stores/course-evaluation-template-edit-summary-store';
import type {CourseEvaluationTemplatePreviewStore} from 'views/signed-in/courses/stores/course-evaluation-template-preview-store';
import type {CourseEvaluationTemplateStore} from 'views/signed-in/courses/stores/course-evaluation-template-store';
import type {CourseListStore} from 'views/signed-in/courses/stores/course-list-store';
import type {CourseReorderCurriculumStore} from 'views/signed-in/courses/stores/course-reorder-curriculum-store';
import type {CourseStore} from 'views/signed-in/courses/stores/course-store';
import type {CoursesBulkUpdateStore} from 'views/signed-in/courses/stores/courses-bulk-update-store';
import type {ProfileStore} from 'views/signed-in/profile/stores/profile-store';
import type {ScenarioDetailStore} from 'views/signed-in/scenarios/stores/scenario-detail-store';
import type {ScenarioEvalTemplateEvalsStore} from 'views/signed-in/scenarios/stores/scenario-eval-template-evals-store';
import type {ScenarioEvaluationEditStore} from 'views/signed-in/scenarios/stores/scenario-evaluation-edit-store';
import type {ScenarioEvaluationTemplatePreviewStore} from 'views/signed-in/scenarios/stores/scenario-evaluation-template-preview-store';
import type {ScenarioEvaluationTemplateStore} from 'views/signed-in/scenarios/stores/scenario-evaluation-template-store';
import type {ScenarioImportStore} from 'views/signed-in/scenarios/stores/scenario-import-store';
import type {ScenariosStore} from 'views/signed-in/scenarios/stores/scenarios-store';
import type {ReservationEditOverviewStore} from 'views/signed-in/schedule/stores/reservation-edit-overview-store';
import type {ReservationEditPeopleStore} from 'views/signed-in/schedule/stores/reservation-edit-people-store';
import type {ReservationEditRoomSetupStore} from 'views/signed-in/schedule/stores/reservation-edit-room-setup-store';
import type {ReservationNewStore} from 'views/signed-in/schedule/stores/reservation-new-store';
import type {ReservationRequestNewStore} from 'views/signed-in/schedule/stores/reservation-request-new-store';
import type {ReservationStore} from 'views/signed-in/schedule/stores/reservation-store';
import type {LiveReservationSelectionStore} from 'views/signed-in/sessions/stores/live-reservation-selection-store';
import type {LiveSessionStore} from 'views/signed-in/sessions/stores/live-session-store';
import type {SessionImportStore} from 'views/signed-in/sessions/stores/session-import-store';
import type {SessionViewerStore} from 'views/signed-in/sessions/stores/session-viewer-store';
import type {SessionBulkUpdateStore} from 'views/signed-in/sessions/stores/sessions-bulk-update-store';
import type {SessionsStore} from 'views/signed-in/sessions/stores/sessions-store';
import type {SsAnnotationsStore} from 'views/signed-in/system-setup/stores/ss-annotations-store';
import type {SsGeneralStore} from 'views/signed-in/system-setup/stores/ss-general-store';
import type {SSLibrariesStore} from 'views/signed-in/system-setup/stores/ss-libraries-store';
import type {SsUsersSecurityStore} from 'views/signed-in/system-setup/stores/ss-users-security-store';
import type {UserDetailStore} from 'views/signed-in/users/stores/user-detail-store';
import type {UserListStore} from 'views/signed-in/users/stores/user-list-store';

const GlobalReducer: ReducersMapObject = {};

export let store: Store<DefaultRootState>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ActionCallback<S, A extends Action = any> = (state?: S, action?: A) => void | Partial<S>;

export type ActionsMap<S> = {[key: string]: ActionCallback<S>};

export function createReducer<S>(initialState: S, actionsMap: ActionsMap<S>): Reducer<S> {
    return (state: S = initialState, action: Action): S => {
        const reduceFn = actionsMap[action.type];
        if (!reduceFn) {
            return state;
        }

        return Object.assign<Partial<S>, S, Partial<S>>(
            {},
            state,
            // @ts-expect-error TS2345: Argument of type void | Partial<S> is not assignable to parameter of type Partial<S>
            // Type void is not assignable to type Partial<S>
            reduceFn(state, action),
        );
    };
}

export function connectReducer<ReducerName extends keyof DefaultRootState>(
    name: ReducerName,
    reducer: Reducer<DefaultRootState[ReducerName]>,
): void {
    GlobalReducer[name] = reducer;

    if (!store) {
        return;
    }
    const newReducer = combineReducers<DefaultRootState>(GlobalReducer);
    store.replaceReducer(newReducer);
}

export function initializeReduxStore<HistoryLocationState>(history: History<HistoryLocationState>) {
    const routerSyncMiddleware = routerMiddleware(history);
    const middlewares = [routerSyncMiddleware, reduxThunk, apiMiddleware, externalMiddleware, socketIOMiddleware];
    const deployment = __DEPLOYMENT__;

    let enhancer = applyMiddleware(...middlewares);
    /**
     * @todo    SCLD-14919 deployment should never be undefined. It currently is
     *          undefined when running unit tests. Remove this temppoary fix.
     */
    if (deployment === undefined) {
        // do nothing. Remove this check.
    } else if (!deployment.includes('production')) {
        enhancer = composeWithDevTools(enhancer);
    } else {
        enhancer = compose(enhancer);
    }

    connectReducer('router', connectRouter(history));
    const reducer = combineReducers<DefaultRootState>(GlobalReducer);

    store = createStore(reducer, enhancer);
    return store;
}

export const connect: Connect<DefaultRootState> = reactReduxConnect;

type MiddlewareDispatch = (dispatch: Dispatch | AnyAction) => AnyAction;

export const useDispatch: () => MiddlewareDispatch = reactReduxUseDispatch;

/**
 * Defines DefaultRootState as state by default without requiring generics.
 */
interface UseSelector {
    <Selected = unknown>(selector: (state: DefaultRootState) => Selected, equalityFn?: EqualityFn<Selected>): Selected;
    <Selected = unknown>(
        selector: (state: DefaultRootState) => Selected,
        options?: UseSelectorOptions<Selected>,
    ): Selected;
}

export const useSelector: UseSelector = reactReduxUseSelector;

export type DefaultRootState = {
    app: AppStore;
    course: CourseStore;
    courseAgenda: CourseAgendaCalendarStore;
    courseEditAdmins: CourseEditAdminsStore;
    courseEditAttachments: CourseEditAttachmentsStore;
    courseEvalTemplateEvals: CourseEvalTemplateEvalsStore;
    courseEvaluationEditStore: CourseEvaluationEditStore;
    courseEvaluationTemplate: CourseEvaluationTemplateStore;
    courseEvaluationTemplateEdit: CourseEvaluationTemplateEditSummaryStore;
    courseEvaluationTemplatePreview: CourseEvaluationTemplatePreviewStore;
    courseList: CourseListStore;
    courseReorderCurriculum: CourseReorderCurriculumStore;
    coursesBulkUpdate: CoursesBulkUpdateStore;
    examsErrors: ExamsErrorsStore;
    intl: IntlStore;
    live: LiveSessionStore;
    liveReservationSelection: LiveReservationSelectionStore;
    modals: ModalsStore | ExamModalsStore;
    profile: ProfileStore;
    publicSessionViewer: PublicSessionViewerStore;
    reservation: ReservationStore;
    reservationEditOverview: ReservationEditOverviewStore;
    reservationEditPeople: ReservationEditPeopleStore;
    reservationEditRoomSetup: ReservationEditRoomSetupStore;
    reservationNew: ReservationNewStore;
    reservationRequestNew: ReservationRequestNewStore;
    router: unknown;
    scenarioEvalTemplateEvals: ScenarioEvalTemplateEvalsStore;
    scenarioEvaluationEditStore: ScenarioEvaluationEditStore;
    scenarioEvaluationTemplate: ScenarioEvaluationTemplateStore;
    scenarioEvaluationTemplatePreview: ScenarioEvaluationTemplatePreviewStore;
    scenarioDetail: ScenarioDetailStore;
    scenarioImport: ScenarioImportStore;
    scenarios: ScenariosStore;
    session: SessionStore;
    sessionsBulkUpdate: SessionBulkUpdateStore;
    sessionImport: SessionImportStore;
    sessions: SessionsStore;
    sessionViewer: SessionViewerStore;
    ssAnnotations: SsAnnotationsStore;
    ssGeneral: SsGeneralStore;
    SSLibrariesStore: SSLibrariesStore;
    ssUsersSecurity: SsUsersSecurityStore;
    userDetail: UserDetailStore;
    userList: UserListStore;
    viewer: ViewerStore;
};
